/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { branch, compose, renderComponent, withProps } from 'recompose';
import { Table, OverlayTrigger, Glyphicon, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { getUserTimesheetLink } from '../../timesheetNavigator/links';

import queryMetaHandler from '../../../utils/queryMetaHandler';
import omitProps from '../../../utils/omitProps';
import NewTabLink from '../../../components/NewTabLink';
import formatNumberTwoDecimals from '../../../utils';

const propTypes = exact({
  /* eslint-disable react/no-unused-prop-types */
  ticketId: PropTypes.number.isRequired,
  ticket: PropTypes.object.isRequired,
  viewer: PropTypes.bool,
  /* eslint-enable react/no-unused-prop-types */
});

const tooltip = (notes) => {
  return (
    <Tooltip id="tooltip" placement="top">
      {notes}
      {/* <strong>This ticket can not be changed.</strong> Ticket has already been processed. */}
    </Tooltip>
  );
};


const TicketUserHourSummary = (props) => {
  const { user_daily_time_entry_groups, department } = props.ticket;
  const { edges } = user_daily_time_entry_groups;
  const { interval } = user_daily_time_entry_groups.time_entry_group;

  const tableHeaders = [
    'Name',
    ...interval.map((day) => { return `${day.day_of_week} ${day.month}-${day.day}`; }),
    'Total Hours',
  ];

  const renderLinkableHours = (url, day) => {
    const { viewer } = props;
    const formattedHours = formatNumberTwoDecimals(day.worked_hours.to_hours);

    if (viewer) {
      return <div>{formattedHours}</div>;
    }
    return <NewTabLink style={{ display: 'inline' }} to={url}>{formattedHours}</NewTabLink>;
  };

  const renderTotalLinkableHours = (url, day, isWellServiceAndWorkedOtherHours) => {
    const { viewer } = props;
    const formattedHours = formatNumberTwoDecimals(day.totalHoursRecordedForDay.to_hours);

    if (viewer) {
      return <div>{formattedHours} hr</div>;
    }
    return (
      <div style={{ display: 'inline', marginLeft: '2px' }}>
        (
        <NewTabLink style={{ display: 'inline', backgroundColor: (isWellServiceAndWorkedOtherHours) ? 'yellow' : '' }} to={url}>
          <span>{formattedHours}</span>
        </NewTabLink>
        ) hr
      </div>
    );
  };

  const renderNewTabTime = (day, userId) => {
    const { timeEntries } = day;
    const paidPerDiem = (timeEntries.length > 0) ? timeEntries[0].paidPerDiem : false;
    const isWellService = (department && department.name === 'Well Service');

    const { totalHoursRecordedForDay, worked_hours: workedHours } = day;

    const isWorkedOtherJobs = (totalHoursRecordedForDay.total_seconds !== workedHours.total_seconds);
    const isWellServiceAndWorkedOtherHours = (isWellService && isWorkedOtherJobs);

    const url = getUserTimesheetLink(userId, moment(day.date.iso8601));
    return (
      <div>
        {renderLinkableHours(url, day)}
        {renderTotalLinkableHours(url, day, isWellServiceAndWorkedOtherHours)}
        {paidPerDiem && (
          <OverlayTrigger placement="top" overlay={tooltip('Per Diem Paid')}>
            <Glyphicon glyph="usd" />
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const firstInitial = (firstname) => {
    return firstname.charAt(0);
  };

  const renderRow = (edge) => {
    const user_hours = edge.node;
    const { id, firstname, lastname } = user_hours.user;
    const { user_worked_hours_total, user_daily_time_entries } = user_hours;

    return (
      <tr key={id}>
        <td>
          {firstInitial(firstname)}. {lastname}
        </td>
        {user_daily_time_entries.map((day) => {
          return (
            <td key={day.date.iso8601}>
              {renderNewTabTime(day, id)}
            </td>
          );
        })}
        <td>
          <span style={{ fontWeight: 'bold' }}>{formatNumberTwoDecimals(user_worked_hours_total.to_hours)} hr</span>
        </td>
      </tr>
    );
  };

  const renderTotals = (inputData) => {
    const { time_entry_group } = inputData;
    const { daily_time_entries, total_hours } = time_entry_group;
    return (
      <tr>
        <td style={{ fontWeight: 'bold' }}>Daily Totals</td>
        {daily_time_entries.map((dayObject) => {
          const { display } = dayObject.date;
          return (
            <td style={{ fontWeight: 'bold' }} key={display}>
              {formatNumberTwoDecimals(dayObject.total_hours.to_hours)} hr
            </td>
          );
        })}
        <td style={{ fontWeight: 'bold' }}>{formatNumberTwoDecimals(total_hours.to_hours)} hr</td>
      </tr>
    );
  };

  return (
    <Table>
      <thead>
        <tr>
          {tableHeaders.map((header) => { return <th key={header}>{header}</th>; })}
        </tr>
      </thead>
      <tbody>
        {edges.map(renderRow, this)}
      </tbody>
      <tfoot>
        {renderTotals(user_daily_time_entry_groups)}
      </tfoot>
    </Table>
  );
};


const Query = gql`
  query TicketUserHoursByDay($ticketId: Int!) {
    ticket(ticket_id: $ticketId) {
      id
      department {
        id
        name
      }
      user_daily_time_entry_groups {
        time_entry_group {
          interval {
            day_of_week
            day
            month
            iso8601
          }
          total_hours {
            to_string
            to_hours
          }
          daily_time_entries {
            date {
              display
            }
            total_hours {
              to_string
              to_hours
            }
          }
        }
        edges {
          node {
            user {
              id
              firstname
              lastname
            }
            user_worked_hours_total: worked_hours {
              to_string
              to_hours
            }
            user_daily_time_entries {
              timeEntries: time_entries {
                paidPerDiem: paid_per_diem
              }
              date {
                iso8601
              }
              worked_hours {
               to_string
               to_hours
               total_seconds
              }
              totalHoursRecordedForDay: total_hours_recorded_for_day {
                to_string
                to_hours
                total_seconds
              }
            }
          }
        }
      }
    }
  }
`;


TicketUserHourSummary.propTypes = propTypes;

TicketUserHourSummary.defaultProps = {
  viewer: false,
};


const withData = graphql(Query, {
  options: { fetchPolicy: 'network-only' },
});


const mapDataToProps = withProps((props) => {
  const { ticket } = props.data;
  return {
    ticket,
  };
});


const withNoTimeEntries = branch(
  (props) => { return props.ticket.user_daily_time_entry_groups === null; },
  renderComponent((props) => {
    return (
      <div className="panel-body alert-danger" style={{ textAlign: 'center' }}>
        <h4>No time entries found</h4>
        <p className="text-muted">Ensure corresponding timesheets have been processed.</p>
      </div>
    );
  }),
);


const enhance = compose(
  withData,
  queryMetaHandler,
  mapDataToProps,
  omitProps(['data']),
  withNoTimeEntries,
);


export default enhance(TicketUserHourSummary);
